/**
 * The Artwork section, headless (artwork spec 15 §7): the `artwork` field of an
 * artwork record (one line and version; an order's, a quote's or a repeatable item's) laid out line
 * by line, each version joined to its row in the Artwork step when the record
 * carries the step. Where a version stands (its node and its sentence) is the
 * backend's: the step's detail item carries the state and problem, and
 * `status_text` the words. This never recomputes a stage from files and proofs.
 */
import type {
  ItemArtworkView,
  OrderArtworkView,
  QuoteArtworkView,
  StaffArtworkLine,
  StaffVersionArtwork,
} from "../data/artwork-staff-types";
import type {
  LifecycleStep,
  RecordView,
  StepDetailItem,
} from "./object-ui/contract";

/** Preparation's step on the order (its `lifecycle_steps`). */
export const ARTWORK_STEP_ID = "artwork_preparation.order_artwork";

/** A version as the section shows it, with the node the Artwork step gives it. */
export interface SectionVersion {
  version: StaffVersionArtwork;
  /** ".03 · A Exeter": the step's label for the version. */
  label: string;
  /** The order line's name. */
  lineName: string | null;
  /** The step's state and problem for the version; null when the step lists none. */
  node: Pick<StepDetailItem, "state" | "problem"> | null;
}

export interface SectionLine {
  line: StaffArtworkLine;
  versions: SectionVersion[];
}

export interface OrderArtworkSection {
  /** The step's own status lines, joined ("2 of 6 approved · 1 can't print"); null when the step is not sent. */
  summary: string | null;
  lines: SectionLine[];
  /** Whether any line needs artwork; otherwise the section says why each line needs none. */
  hasVersions: boolean;
}

/** The section's data, or null while the record carries no artwork (the artwork family is not wired). */
export function orderArtworkSectionOf(
  record: RecordView,
): OrderArtworkSection | null {
  const artwork = record["artwork"] as
    OrderArtworkView | QuoteArtworkView | ItemArtworkView | null | undefined;
  if (!artwork) return null;
  const step = (record.steps ?? []).find((s) => s.step === ARTWORK_STEP_ID);
  const detail = new Map((step?.detail ?? []).map((d) => [d.id, d]));
  const lines = artwork.lines.map((line) => ({
    line,
    versions: line.versions.map((version) =>
      versionOf(line, version, detail.get(version.version_id)),
    ),
  }));
  return {
    summary: summaryOf(step),
    lines,
    hasVersions: lines.some((l) => l.versions.length > 0),
  };
}

function versionOf(
  line: StaffArtworkLine,
  version: StaffVersionArtwork,
  item: StepDetailItem | undefined,
): SectionVersion {
  return {
    version,
    label:
      item?.label ??
      [line.line_no, version.display].filter(Boolean).join(" · "),
    lineName: item?.name ?? null,
    node: item ? { state: item.state, problem: item.problem } : null,
  };
}

function summaryOf(step: LifecycleStep | undefined): string | null {
  const lines = step?.status_lines ?? [];
  return lines.length > 0 ? lines.join(" · ") : null;
}

/** Why the lines need no artwork, once each: "No artwork: a service". */
export function noArtworkSentences(section: OrderArtworkSection): string[] {
  return [
    ...new Set(
      section.lines.flatMap(({ line }) =>
        line.none_text ? [line.none_text] : [],
      ),
    ),
  ];
}
